import { Button, ButtonProps } from '@insight-health-ai/roseate/components';
import { RefreshCw01 } from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';

type ErrorScreenProps = {
  style?: React.CSSProperties;
  className?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  retryButton?: Pick<
    ButtonProps,
    'label' | 'onClick' | 'appearance' | 'disabled' | 'loading' | 'icon'
  >;
  errorType?: 'network' | 'server' | 'permission' | 'notFound' | 'generic';
};

export const ErrorScreen = ({
  style,
  className,
  title,
  description,
  retryButton,
  errorType = 'generic',
}: ErrorScreenProps) => {
  const getDefaultContent = (type: string) => {
    switch (type) {
      case 'network':
        return {
          title: 'Connection issue',
          description: 'Please check your internet connection and try again.',
        };
      case 'server':
        return {
          title: 'Service temporarily unavailable',
          description:
            'Our servers are experiencing issues. Please try again in a few moments.',
        };
      case 'permission':
        return {
          title: 'Access denied',
          description:
            "You don't have permission to view this content. Please contact your administrator.",
        };
      case 'notFound':
        return {
          title: 'Not found',
          description:
            'The requested information could not be found. It may have been moved or deleted.',
        };
      default:
        return {
          title: 'Oops! Something went wrong',
          description:
            "We've let our engineers know. Please try again or contact support if the issue persists.",
        };
    }
  };

  const defaultContent = getDefaultContent(errorType);
  return (
    <div
      className={classNames(
        'flex h-full w-full flex-col gap-3 overflow-hidden items-center justify-center',
        className
      )}
      style={style}
    >
      <img
        className="max-w-[320px]"
        src="/assets/illustrations/error.svg"
        alt="error"
      />
      <div className="flex flex-col gap-1 items-center justify-center max-w-[480px]">
        <div className="text-xl font-semibold text-center">
          {title || defaultContent.title}
        </div>

        <div className="text-gray-500 text-center">
          {description || defaultContent.description}
        </div>
      </div>
      {retryButton?.onClick && (
        <Button
          variant="outlined"
          label="Retry"
          icon={<RefreshCw01 />}
          {...retryButton}
        />
      )}
    </div>
  );
};
